import {IMessenger, IMessengerChannel, IMessengerMessage} from "../PA.Core.IF.Basic";
import {BehaviorSubject} from "rxjs";
import * as _ from "lodash"
import {v4} from 'uuid'
import * as AGChannel from "ag-channel"
import {PAWebSocketClient} from "../PAWebSocketClient";

export interface IMessengerCreateOptions {
    ChannelId?: string
    ChannelName?: string
    ChannelGroup?: string
    InBoxEnabled?: boolean
    InboxMaxSize?: number,
    bDebug?: boolean
}

const DefaultCreateOptions: IMessengerCreateOptions = {
    InBoxEnabled: false,
    InboxMaxSize: 3
}

export class Messenger<MT extends IMessengerMessage>
    implements IMessenger<MT>
{
    private _channelId: string
    private _channelName: string
    private _channelGroup: string

    private _outboxLastMessage: MT

    private _bInboxEnabled: boolean
    private _inboxMessages: MT[]
    private _inboxMaxMessageCount: number
    private _OnInBoxMessageReceived: BehaviorSubject<{ messenger: IMessenger<MT>; message: MT }>
    private websocketClient: PAWebSocketClient;
    private wsChannel: AGChannel<any>;
    private wsChannelEventLoopRunning: boolean

    private bDebug: boolean

    constructor(options?: IMessengerCreateOptions) {
        let opts = options || {}
        _.defaults(opts, DefaultCreateOptions)
        this.bDebug = !!opts.bDebug
        this.websocketClient = undefined
        this.wsChannel = undefined
        this.wsChannelEventLoopRunning = false

        this._channelId = opts.ChannelId || ""
        this._channelName = opts.ChannelName || ""
        this._channelGroup = opts.ChannelGroup || "Unassigned"

        this._outboxLastMessage = undefined

        this._bInboxEnabled = !!(opts.InBoxEnabled)
        this._inboxMessages = []
        this._inboxMaxMessageCount = opts.InboxMaxSize
        this._OnInBoxMessageReceived = new BehaviorSubject<{messenger: IMessenger<MT>; message: MT}>(undefined)

        if(this._bInboxEnabled)
            this.enableInBox()
    }


    //<editor-fold desc="Address, Channel Setup">

    setChannel(channelId?: string, channelName?: string, channelGroup?: string): void {
        if(!_.isEmpty(channelId)) {
            if(this.wsChannel && this.wsChannel.name != channelId){
                this.resetWSChannel()
            }
            this._channelId = channelId
        }

        if(!_.isEmpty(channelName))
            this._channelName = channelName

        if(!_.isEmpty(channelGroup))
            this._channelGroup = channelGroup

    }

    getChannel(): IMessengerChannel {
        this.setupChannelIdIfEmpty()
        return {channelId: this._channelId, channelName: this._channelName, channelGroup: this._channelGroup};
    }
    //</editor-fold>

    //<editor-fold desc="Output,, Transmit API">

    async postMessage(pMsgs: MT[] | MT): Promise<boolean> {
        if(_.isEmpty(pMsgs))
            return

        this.setupChannelIdIfEmpty()

        try {
            await this.setupTransmitChannel()

            if (!this.wsChannel)
                return false

            let arrMesages: MT[]
            if (_.isArray(pMsgs))
                arrMesages = pMsgs
            else
                arrMesages = [pMsgs]
            for (let msg of arrMesages) {
                await this.wsChannel.transmitPublish(msg)
                this._outboxLastMessage = msg
                if(this.bDebug)
                    console.log("Transmit on ", this._channelId, " msg: ", msg)
            }
            return true
        } catch(err) {
            return false
        }
    }

    get OutBoxLastMessage(): MT { return this._outboxLastMessage }

    //</editor-fold>

    //<editor-fold desc="Inbox, Listening API">

    get bInboxEnabled(): boolean { return this._bInboxEnabled }

    async enableInBox(): Promise<void> {
        this.setupChannelIdIfEmpty()
        await this.setupListeningChannel(true)
        this._bInboxEnabled = true
    }

    disableInBox(): void {
        this._bInboxEnabled = false
        this.setupListeningChannel(false)
    }

    get InBoxMessages(): MT[] { return this._inboxMessages}
    get InBoxLastMessage(): MT { return _.last(this._inboxMessages) }
    get InBoxMaxMessageCount(): number { return this._inboxMaxMessageCount }
    get OnInBoxMessageReceived(): BehaviorSubject<{ messenger: IMessenger<MT>; message: MT }> { return this._OnInBoxMessageReceived }

    destroy(): void {
        this._bInboxEnabled = false
        this.resetWSChannel()
    }


    //</editor-fold>

    //<editor-fold desc="Private API">

    private get WebSocketClient() : PAWebSocketClient {
        if (!this.websocketClient) {
            this.websocketClient = new PAWebSocketClient();
        }
        return this.websocketClient;
    }

    private setupChannelIdIfEmpty() {
        if(_.isEmpty(this._channelId))
            this._channelId = v4()

        if(_.isEmpty(this._channelName))
            this._channelName = this._channelId
    }

    private async setupTransmitChannel(): Promise<void> {
        if(!this.wsChannel)
            this.wsChannel = await this.WebSocketClient.getChannel(this._channelId)
    }

    private async setupListeningChannel(bEnabled: boolean): Promise<void> {
        if((!bEnabled) && this.wsChannel){
            if(this.wsChannel.isSubscribed(true))
                this.wsChannel.unsubscribe()
            return
        }
        if(bEnabled){
            if(!this.wsChannel){
                this.wsChannel = await this.WebSocketClient.getChannel(this._channelId)
            }
            if(!this.wsChannel.isSubscribed(true))
                this.wsChannel.subscribe({})

            this.runListenerEventLoop()
        }

    }

    private runListenerEventLoop(){
        const self = this
        if(self.wsChannelEventLoopRunning)
            return

        if(!self.wsChannel)
            return

        self.wsChannelEventLoopRunning = true;

        (async ()=> {
            for await (let msg of self.wsChannel){

                if(!self._bInboxEnabled)
                    break;

                if(!self.wsChannel)
                    break;

                if(self.wsChannel.name != self._channelId)
                    break;

                //-- Insert into to Inbox
                self._inboxMessages.unshift(msg)
                if(self._inboxMessages.length > self._inboxMaxMessageCount)
                    self._inboxMessages.length = self._inboxMaxMessageCount
                self.OnInBoxMessageReceived.next({messenger: self, message: msg})
                if(self.bDebug)
                    console.log("Received on ", self._channelId, " msg: ", msg)

            }

            self.wsChannelEventLoopRunning = false

        })();

    }

    private resetWSChannel(){
        if(this.wsChannel) {
            this.wsChannel.unsubscribe()
            this.wsChannel = undefined
            this.wsChannelEventLoopRunning = false
        }
    }

    //</editor-fold>


}